import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setMessageHidden,
  setMessageStatusEditing,
  setMessageStatusRemoved,
} from "actions/conversationInbox";
import { Message } from "types/conversationInbox";
import { TooltipWrapper } from "ui";
import { useTrackEvent } from "libs/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const MessageActionContainer = ({
  children,
  position,
  disabledWithTooltip,
}: {
  children: (closeModal: () => void) => React.ReactNode;
  position: "left top" | "right top" | string;
  disabledWithTooltip?: string;
}) => {
  const [open, setOpen] = useState(false);
  const closeModal = () => setOpen(false);

  if (disabledWithTooltip) {
    return (
      <TooltipWrapper tooltip={disabledWithTooltip} placement="top">
        <span className="inline-block cursor-default px-2.5 py-1.5 -mx-2.5 opacity-50">
          <i className="fa-solid fa-ellipsis-vertical text-xs text-neutral-700 align-[2px]" />
        </span>
      </TooltipWrapper>
    );
  }

  const align = position.startsWith("left") ? "start" : "end";

  return (
    <div
      className="mt-1.5 message-more-actions-container"
      data-testid="message-more-options"
    >
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button className="inline-block cursor-pointer px-2.5 py-1.5 -mx-2.5 bg-transparent border-none outline-none">
            <i className="fa-solid fa-ellipsis-vertical text-xs text-neutral-700 align-[2px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} side="top" className="py-2">
          {children(closeModal)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface MessageMoreOptionsProps {
  conversationId: string;
  messageId: string;
  messageHidden: boolean;
  isHiddenProcessing: boolean;
  supportHiddenAction: boolean;
  supportDeleteAction?: boolean;
  isSending?: boolean;
  platform: string;
}

export const MessageMoreOptions = ({
  messageId,
  messageHidden,
  conversationId,
  isHiddenProcessing,
  supportHiddenAction,
  supportDeleteAction = true,
  isSending,
  platform,
}: MessageMoreOptionsProps): JSX.Element => {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  return (
    <MessageActionContainer
      position="right top"
      disabledWithTooltip={
        isSending
          ? "Still sending... You can delete or hide once sent"
          : undefined
      }
    >
      {(closeModal: () => void) => (
        <>
          {supportDeleteAction && (
            <DropdownMenuItem
              data-testid="moreOption-delete-button"
              className="cursor-pointer px-4 py-1.5 text-xs font-normal"
              onClick={() => {
                dispatch(
                  setMessageStatusRemoved({ conversationId, id: messageId })
                );
                closeModal();
                trackEvent({
                  eventName: "comment-delete",
                  useServices: ["AMP"],
                  metadata: { platform },
                });
              }}
            >
              Delete comment on post
            </DropdownMenuItem>
          )}
          {supportHiddenAction && (
            <DropdownMenuItem
              disabled={isHiddenProcessing}
              data-testid="moreOption-hide-button"
              className={cn(
                "cursor-pointer px-4 py-1.5 text-xs font-normal",
                isHiddenProcessing && "text-gray-400"
              )}
              onClick={() => {
                if (isHiddenProcessing) {
                  return;
                }
                dispatch(
                  setMessageHidden({
                    conversationId,
                    id: messageId,
                    hidden: !messageHidden,
                  })
                );
                closeModal();
                trackEvent({
                  eventName: messageHidden ? "comment-unhide" : "comment-hide",
                  useServices: ["AMP"],
                  metadata: { platform },
                });
              }}
            >
              {resolveMessageHiddenAction({
                hidden: messageHidden,
                isHiddenProcessing,
              })}
            </DropdownMenuItem>
          )}
        </>
      )}
    </MessageActionContainer>
  );
};

export const GMBMessageMoreOptions = ({
  conversationId,
  message,
  position = "left",
  label,
}: {
  conversationId: string;
  message: Message;
  position: "left" | "right";
  label: string;
}) => {
  const dispatch = useDispatch();
  return (
    <MessageActionContainer position={`${position} top`}>
      {(closeModal: () => void) => (
        <>
          <DropdownMenuItem
            className="cursor-pointer px-4 py-1.5 text-xs font-normal"
            onClick={() => {
              dispatch(
                setMessageStatusRemoved({ conversationId, id: message.id })
              );
              closeModal();
            }}
          >
            {`Delete reply to ${label}`}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer px-4 py-1.5 text-xs font-normal"
            onClick={() => {
              dispatch(
                setMessageStatusEditing({
                  message,
                  action: "enable",
                })
              );
              closeModal();
            }}
          >
            {`Edit reply to ${label}`}
          </DropdownMenuItem>
        </>
      )}
    </MessageActionContainer>
  );
};

function resolveMessageHiddenAction({
  isHiddenProcessing,
  hidden,
}: {
  hidden?: boolean;
  isHiddenProcessing?: boolean;
}) {
  if (isHiddenProcessing) {
    return "Processing hidden message action";
  }
  if (hidden) {
    return "Unhide comment on post";
  }
  return "Hide comment on post";
}
